// Der Zugangszustand als RAHMENzustand (ST-UX-001).
//
// Er lag bisher in `main.tsx` und gehoerte damit einem einzelnen Bildschirm.
// Das war richtig, solange es einen einzigen gab, und wird falsch, sobald es
// zwei sind: Anmeldung, Tokenerneuerung, Sitzungsablauf und Nichterreichbarkeit
// betreffen die ganze Anwendung, nicht eine Ansicht darin. NFR-UX-005 verlangt
// Fehlererholung; sie je Bildschirm neu zu erfinden ist der zuverlaessigste Weg,
// sie beim dritten zu vergessen.
//
// `fetch` wird hereingereicht statt global benutzt. Der Grund ist der Nachweis:
// so laesst sich die Uebersetzung von Antwort zu Zustand ohne Browser und ohne
// Netz pruefen -- einschliesslich der 403-Antwort, die keine Rollen traegt.

import { plainMessageFor, selfAccessUnreadableMessage, type PlainMessage, type ResponseFacts } from '../access.ts';
import { PLATFORM_DIAGNOSTICS_ROLE_CODE, ROLES_UNKNOWN, rolesKnown, type KnownRoles } from './navigation.ts';

export interface AccessSnapshot {
  /**
   * `denied` ist eine ENTSCHEIDUNG, `failed` eine STOERUNG. Der Unterschied
   * bestimmt, ob ein erneuter Versuch etwas aendern kann, und gehoert deshalb
   * auf den Bildschirm und nicht nur ins Protokoll.
   */
  readonly kind: 'permit' | 'denied' | 'failed' | 'unreadable';
  readonly message: PlainMessage;
  /**
   * Die Rolle, die fuer die Uebersicht fehlt -- nur bei `unreadable`.
   *
   * Sie kommt NICHT aus der Antwort: ein Problemdokument nennt die Regel nicht
   * (Security-Baseline Punkt 6). Sie kommt aus dem Wissen, welche Route der
   * Rahmen gerufen hat und was diese Route verlangt.
   */
  readonly requiredRoleCode?: string;
  readonly areaCount: number;
  /**
   * Was der Rahmen ueber die Rollen weiss -- ausdruecklich auch "nichts".
   * Siehe die Begruendung an `KnownRoles` in navigation.ts.
   */
  readonly roles: KnownRoles;
  readonly correlationId: string | undefined;
  /** Rohantwort, ausschliesslich fuer die technische Sicht. */
  readonly raw: unknown;
}

/** Nur das, was diese Datei von `fetch` braucht. */
export type FetchLike = (input: string, init?: { readonly headers: Record<string, string> }) => Promise<{
  readonly ok: boolean;
  readonly status: number;
  json: () => Promise<unknown>;
}>;

function stringOrUndefined(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function stringList(value: unknown): readonly string[] {
  return Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : [];
}

export async function fetchAccessSnapshot(
  baseUrl: string,
  accessToken: string,
  fetchImpl: FetchLike,
): Promise<AccessSnapshot> {
  const response = await fetchImpl(`${baseUrl}/platform/access-decision`, {
    headers: { authorization: `Bearer ${accessToken}` },
  });
  const body: unknown = await response.json().catch(() => undefined);
  const record = (body ?? {}) as Record<string, unknown>;
  const correlationId = stringOrUndefined(record['correlationId']);

  if (response.ok) {
    const principal = (record['principal'] ?? {}) as Record<string, unknown>;
    return {
      kind: 'permit',
      message: {
        headline: 'Dein Zugang ist eingerichtet.',
        explanation: 'LIAISON kennt dich, deine Rolle und die Bereiche, für die du freigeschaltet bist.',
        nextStep: 'Du kannst arbeiten. Was du tun darfst, steht links in der Navigation.',
        retryable: false,
      },
      areaCount: stringList(principal['operatingContextIds']).length,
      roles: rolesKnown(stringList(principal['roleCodes'])),
      correlationId,
      raw: body,
    };
  }

  // HIER liegt der Punkt, an dem der Rahmen fast falsch geworden waere.
  //
  // Diese Route verlangt SELBST eine Rolle: `Liaison.Platform.Diagnostics.Read`
  // (apps/api/src/policy/platform-rules.ts). Fehlt sie, antwortet der Server mit
  // einem Problemdokument -- ohne `principal` und damit ohne Rollencodes
  // (apps/api/src/http/server.ts, sendProblem 'forbidden').
  //
  // Daraus "diese Person hat keine Rollen" zu folgern waere ein Fehlschluss mit
  // sichtbarer Folge: wer `Liaison.Party.Capture.Write` besitzt, aber die
  // Selbstdiagnose nicht lesen darf, saehe die Schnellerfassung nicht -- obwohl
  // der Server sie ihm erlauben wuerde. Die Rollen sind in diesem Fall nicht
  // leer, sondern UNBEKANNT.
  // WAS DIE ANTWORT UEBER SICH SELBST HERGIBT, statt einer Pauschale.
  //
  // `answered` ist die Frage, ob sich die Antwort ueberhaupt als Auskunft von
  // LIAISON lesen laesst. `response.json()` scheitert an allem, was kein JSON
  // ist -- an der 404-Seite des statischen Hosts von heute genauso wie an der
  // Wartungsseite eines Zwischenspeichers von morgen. Das ist die Eigenschaft,
  // auf die es ankommt; der Medientyp ist nur eine ihrer Auspraegungen.
  const answered = typeof body === 'object' && body !== null;
  const facts: ResponseFacts = {
    status: response.status,
    answered,
    hasCorrelationId: correlationId !== undefined,
  };
  const reasonCode = stringOrUndefined(record['reasonCode']);

  // DER VIERTE ZUSTAND: die Uebersicht ist nicht lesbar, die Person ist nicht
  // abgelehnt. `no_matching_rule` an DIESER Route heisst genau eines -- die
  // Diagnoserolle fehlt --, weil die Route nur eine Regel hat
  // (apps/api/src/policy/platform-rules.ts) und jede andere Ablehnung einer
  // verifizierten Identitaet (Sperrvermerk, fehlende Bereichszuordnung,
  // Gruppenclaim) ihren eigenen Grundcode traegt. Die bleiben `denied`: sie
  // betreffen die Person und nicht die Uebersicht.
  //
  // Bis zum 14.09.2026 fiel dieser Fall in `denied` mit dem Satz der
  // Fachrouten. Da die Rolle nur auf der Diagnostics-Gruppe liegt, war das der
  // Startbildschirm JEDER gewoehnlichen Nutzerin.
  if (response.status === 403 && reasonCode === 'no_matching_rule') {
    return {
      kind: 'unreadable',
      message: selfAccessUnreadableMessage(),
      areaCount: 0,
      roles: ROLES_UNKNOWN,
      requiredRoleCode: PLATFORM_DIAGNOSTICS_ROLE_CODE,
      correlationId,
      raw: body,
    };
  }

  return {
    // Eine Antwort ohne lesbare Auskunft ist KEINE Entscheidung. Sie als
    // `denied` zu fuehren hiesse, LIAISON habe abgelehnt -- LIAISON hat gar
    // nichts gesagt. Der Unterschied traegt bis auf den Bildschirm: `denied`
    // faerbt die Meldung als Ablehnung, `failed` als Stoerung.
    kind: response.status >= 500 || !answered ? 'failed' : 'denied',
    message: plainMessageFor(reasonCode, facts),
    areaCount: 0,
    roles: ROLES_UNKNOWN,
    correlationId,
    raw: body,
  };
}
